import { useEffect, useState } from 'react';

import type {
  FullHeadMPRSession,
  FullHeadMPRUIEvents,
  ToothSliceGeometryMode,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { MedicalImageRender } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { FullHeadMPRToothModeState } from '../config';
import {
  readToothSliceMode,
  writeToothSliceMode,
  type ToothSliceModeScope,
} from '../lib';

type UseToothSliceModeParams = {
  isReady: boolean;
  scope: ToothSliceModeScope;
  defaultGeometryMode?: ToothSliceGeometryMode;
  session?: FullHeadMPRSession | null;
};

type UseToothSliceModeResult = {
  toothMode: FullHeadMPRToothModeState;
  setGeometryMode: (mode: ToothSliceGeometryMode) => void;
};

export const useToothSliceMode = (
  params: UseToothSliceModeParams,
): UseToothSliceModeResult => {
  const { isReady, scope, defaultGeometryMode, session } = params;

  const [toothMode, setStoredMode] = useState<FullHeadMPRToothModeState>(
    () => ({
      geometryMode: readToothSliceMode({
        fallback: defaultGeometryMode,
        scope,
      }),
      isActive: false,
      renderPath: null,
      roiStatus: 'idle',
      toothIso: null,
    }),
  );

  useEffect(() => {
    if (!isReady) {
      setStoredMode((current) => ({
        ...current,
        isActive: false,
        renderPath: null,
        roiStatus: 'idle',
        toothIso: null,
      }));
      return;
    }

    const onModeChanged = (
      event: FullHeadMPRUIEvents['fullHeadMPRToothModeChanged'],
    ) => {
      setStoredMode({
        geometryMode: event.geometryMode,
        isActive: event.active,
        renderPath: event.renderPath,
        roiStatus: event.roiStatus,
        toothIso: event.toothIso,
      });
    };
    const removeListener = session
      ? session.addEventListener('fullHeadMPRToothModeChanged', onModeChanged)
      : MedicalImageRender.addEventListener(
          'fullHeadMPRToothModeChanged',
          onModeChanged,
          'fullHeadMPR',
        );
    // A modal session restores its view (and announces it) before the modal
    // reports ready, so the state is read instead of waiting for a re-announce.
    if (session) {
      onModeChanged(session.getToothMode());
    }

    return () => {
      removeListener?.();
    };
  }, [isReady, session]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (session) {
      session.setToothSliceMode(toothMode.geometryMode);
    } else {
      MedicalImageRender.setFullHeadMPRToothSliceMode?.(toothMode.geometryMode);
    }
  }, [isReady, session, toothMode.geometryMode]);

  const setGeometryMode = (mode: ToothSliceGeometryMode) => {
    writeToothSliceMode({ mode, scope });
    setStoredMode((current) => ({ ...current, geometryMode: mode }));
  };

  return { setGeometryMode, toothMode };
};
